import {
  AuditV2APIApiAuditV2APIGetAuditHistogramRequest,
  AuditV2APIApiAuditV2APIGetAuditStatsRequest,
  AuditV2APIApiAuditV2APIListAuditEventsRequest,
  CastaiAuditV1beta1AuditListEntry,
  CastaiAuditV2AuditEvent,
  CastaiAuditV2EventCounter,
} from '@cast/openapi';
import { Cloud, K8sProvider } from '@cast/shared-types';
import { ClusterHibernationJobAuditLogEventType } from '@cast/types-cluster-autoscaler';

export type AuditLogFilter = {
  clusterId?: string;
  labels?: Record<string, string>;
  fromDate?: string; // RFC3339
  toDate?: string; // RFC3339
  initiatedByEmail?: string;
  initiatedById?: string;
  operation?: string;
  page?: {
    limit: number;
    // A cursor for use in pagination. This is a token that defines your place in the list.
    cursor: string;
  };
  /**
   * @deprecated use `page.limit` instead
   * */
  limit?: number;
  /**
   * @deprecated use `page.cursor` instead
   * */
  cursor?: string;
};

export enum EventType {
  POLICY_APPLIED = 'policyApplied',
  POLICY_UPDATED = 'policyConfigUpdated',
  NODE_ADDED = 'nodeAdded',
  NODE_INTERRUPTED = 'nodeInterrupted',
  NODES_UPDATED = 'nodesUpdated',
  NODE_DELETED = 'nodeDeleted',
  NODE_DRAINED = 'nodeDrained',
  CLUSTER_CONNECTED = 'clusterConnected',
  CLUSTER_CREATED = 'clusterCreated',
  CLUSTER_UPDATED = 'clusterUpdated',
  CLUSTER_PAUSED = 'clusterPaused',
  CLUSTER_RESUMED = 'clusterResumed',
  CLUSTER_DELETED = 'clusterDeleted',
  CLUSTER_DISCONNECTED = 'clusterDisconnected',
  CLUSTER_RECONCILE_TRIGGERED = 'clusterReconcileTriggered',
  CLUSTER_HIBERNATION_JOB_FINISHED = 'clusterHibernationJobFinished',
  CLUSTER_HIBERNATION_JOB_PENDING = 'clusterHibernationJobPending',
  CLUSTER_HIBERNATION_JOB_FAILED = 'clusterHibernationJobFailed',
  CLUSTER_HIBERNATION_TRIGGERED = 'clusterHibernateTriggered',
  CLUSTER_HIBERNATION_SCHEDULE_FAILED = 'hibernationScheduleFailedForCluster',
  CLUSTER_HIBERNATED = 'clusterHibernated',
  AUTOSCALER_EXECUTED = 'autoscalerExecuted',
  REBALANCING_PLAN_INITIATED = 'rebalancingPlanInitiated',
  REBALANCING_PLAN_EXECUTED = 'rebalancingPlanExecuted',
  REBALANCING_NODE_OPERATIONS = 'rebalancingNodeOperations',
  REBALANCING_NODE_OPERATION_FINISHED = 'rebalancingNodeOperationFinished',
  REBALANCING_PLAN_FAILED = 'rebalancingPlanFailed',
  NODE_CONFIGURATION_CREATED = 'nodeConfigurationCreated',
  NODE_CONFIGURATION_UPDATED = 'nodeConfigurationUpdated',
  NODE_CONFIGURATION_DELETED = 'nodeConfigurationDeleted',
  NODE_CONFIGURATION_DEFAULT = 'nodeConfigurationAsDefault',
  UNKNOWN_EVENT = 'unknownEvent',
  CLOUD_INSTANCE_CREATED = 'cloudInstanceCreated',
  ADD_BLACKLIST_EXECUTED = 'addBlacklistExecuted',
  NODE_TEMPLATE_UPDATED = 'nodeTemplateUpdated',
  NODE_TEMPLATE_CREATED = 'nodeTemplateCreated',
  NODE_TEMPLATE_DELETED = 'nodeTemplateDeleted',
  SCHEDULED_REBALANCING_SKIPPED = 'scheduledRebalancingJobSkipped',
  COMMITMENT_ASSIGNED = 'commitmentAssigned',
  COMMITMENT_UNASSIGNED = 'commitmentUnassigned',
  COMMITMENT_ASSIGNMENT_CHANGED = 'commitmentAssignmentChanged',
  INTERRUPTION_PREDICTION_NODES_TO_REBALANCE = 'interruptionPredictionNodesToRebalance',
  INTERRUPTION_PREDICTION_RECEIVED = 'interruptionPredictionReceived',
}

export enum PolicyConfig {
  MIN_CORES = 'min_cores',
  MAX_CORES = 'max_cores',
  HEADROOM_MEMORY_PERCENTAGE = 'headroom_memory_percentage',
  HEADROOM_CPU_PERCENTAGE = 'headroom_cpu_percentage',
  HEADROOM_SPOT_CPU_PERCENTAGE = 'headroom_spot_cpu_percentage',
  HEADROOM_SPOT_ENABLED = 'headroom_spot_enabled',
  HEADROOM_SPOT_MEMORY_PERCENTAGE = 'headroom_spot_memory_percentage',
  MAXRECLAIMRATE = 'maxreclaimrate',
  CLOUDS = 'clouds',
  NODE_CONSTRAINTS_ENABLED = 'node_constraints_enabled',
  NODE_CONSTRAINTS_MIN_CPU_CORES = 'node_constraints_min_cpu_cores',
  NODE_CONSTRAINTS_MAX_CPU_CORES = 'node_constraints_max_cpu_cores',
  NODE_CONSTRAINTS_MIN_RAM_MIB = 'node_constraints_min_ram_mib',
  NODE_CONSTRAINTS_MAX_RAM_MIB = 'node_constraints_max_ram_mib',
  DELAY_SECONDS = 'delay_seconds',
  AGGRESSIVE_MODE = 'aggressive_mode',
  PREDICTIONS_TYPE = 'predictions_type',
}

export enum AuditPolicy {
  UNSCHEDULABLE_PODS = 'UnschedulablePods',
  EMPTY_WORKER_NODES = 'EmptyWorkerNodes',
  CLUSTER_LIMITS = 'ClusterLimits',
  SPOT_INSTANCES = 'SpotInstances',
  SPOT_INSTANCES_ALT = 'SPOT_INSTANCES',
  DEAD_NODES = 'DeadNodes',
}

export enum NodeTemplateConfig {
  USE_FALLBACKS = 'use_fallbacks',
  FALLBACK_RESTORE_RATE_SECONDS = 'fallback_restore_rate_seconds',
  ENABLE_SPOT_DIVERSITY = 'enable_spot_diversity',
  SPOT_DIVERSITY_PRICE_INCREASE_LIMIT_PERCENT = 'spot_diversity_price_increase_limit_percent',
  MAX_CPU = 'max_cpu',
  COMPUTE_OPTIMIZED = 'compute_optimized',
  IS_GPU_ONLY = 'is_gpu_only',
  CUSTOM_INSTANCES_ENABLED = 'custom_instances_enabled',
  CUSTOM_INSTANCES_EXTENDED_MEMORY_ENABLED = 'custom_instances_extended_memory_enabled',
  CUSTOM_PRIORITY = 'custom_priority',
}

export enum NodeTemplateSetting {
  CONSTRAINTS = 'Constraints',
  CUSTOM_TAINTS = 'CustomTaints',
  CUSTOM_LABELS = 'CustomLabels',
  NODE_TEMPLATE = 'NodeTemplate',
  SPOT_RESOURCE_OFFERING = 'SpotResourceOffering',
  ON_DEMAND_RESOURCE_OFFERING = 'OnDemandResourceOffering',
}

export enum OperationState {
  CALCULATED = 'calculated',
  EXECUTED = 'executed',
}

export enum OperationEffects {
  ADD = 'add',
  REMOVE = 'delete',
  DRAIN = 'drain',
  PREPARE = 'prepare',
}

export enum AuditStaticAction {
  PAUSED = 'paused',
  READY = 'ready',
  DRAINED = 'drained',
}

type ChangeLog = {
  fields: Array<{
    after: string | number;
    before: string | number;
    name: string;
  }>;
  name: AuditPolicy;
};

export type InitiatedBy = {
  /** User or system ID */
  id: string;
  /** User or system name */
  name?: string;
  email?: string;
};

type AuditError = {
  details: string;
  message: string;
  reason: string;
};

export type AuditNodeAction = {
  state: OperationState;
  effect: OperationEffects;
};

export type AuditNode = {
  id?: string;
  cloud?: Cloud;
  csp?: Cloud;
  name?: string;
  instanceType?: string;
  instance_type?: string;
  role?: any;
  action?: AuditNodeAction;
  spot?: boolean;
  isSpot?: boolean;
  labels?: Record<string, string>;
  spot_config?: {
    is_spot: boolean;
  };
  staticAction?: AuditStaticAction;
  cpu?: number;
  createdAt?: string;
  isSpotFallback?: boolean;
  memoryMiB?: number;
  priceHourly?: string;
  error?: AuditError;
  azName?: string;
};

type AuditLogFeatureLabels = 'spotFallback';

export type AuditLogLabels = {
  clusterId: string;
  feature: AuditLogFeatureLabels;
  rebalancingPlanId: string;
};

type AuditCluster = {
  availabilityZones: Array<{
    id: string;
    name: string;
  }>;
  clusterRegion: string;
  cspRegions: Record<Cloud, string>;
  csps: Cloud[];
  id: string;
  nodes: AuditNode[];
  organizationId: string;
  provider: K8sProvider;
};

export type AuditClusterMinimal = {
  cloudCredentialsIDs: string[];
  id: string;
  name: string;
  region: string;
  nodes: AuditNode[];
};

type AuditExternalClusterMinimal = {
  cloudCredentialsIDs: string;
  id: string;
  name: string;
  providerType: string;
  region: string;
};

type ExternalNodeInterrupt = {
  cluster: AuditExternalClusterMinimal;
  node: AuditNode;
};

type AuditOperationFilter = {
  azId?: string;
  includeARM?: boolean;
  isLocalStorageOptimized?: boolean;
  maxCPU?: number;
  maxMemory?: number;
  minCPU?: number;
  minMemory?: number;
  minStorageGiB?: number;
  regions?: string[];
  spot?: boolean;
  spotReliability?: number;
  subnetID?: string;
};

export type SkippedInstance = {
  azName: string;
  csp: Cloud;
  instanceType: string;
  isSpot: boolean;
  skipReason: string;
};

export type UnschedulablePod = {
  cpuRequests: number;
  memoryRequests: number;
  name: string;
  namespace: string;
};

type NoteTemplate = {
  // more data is returned with event, but only these fields will be used.
  name: string;
  isEnabled: boolean;
  configurationID: string;
  constraints: {
    onDemandOptions: {
      enabled: boolean;
    };
    spotOptions: {
      enabled: boolean;
    };
  };
};

type CustomPriorityRow = {
  family: string[];
  spot?: boolean;
  onDemand?: boolean;
};

export type NodeTemplateChangeLogField = {
  after: string | number;
  before: string | number;
  name: string;
};

export type NodeTemplateCustomPriorityChangeLogField = {
  after: CustomPriorityRow[] | { include: string[] } | null;
  before: CustomPriorityRow[] | { include: string[] } | null;
  name: string;
};

type NodeTemplateChangeLog = {
  fields: Array<
    NodeTemplateChangeLogField | NodeTemplateCustomPriorityChangeLogField
  >;
  name: string;
};

type Trigger = {
  unschedulablePods?: UnschedulablePod[];
};

export type AuditOperation = {
  effect: OperationEffects;
  node: AuditNode;
  policy: 'EmptyWorkerNodes' | 'UnschedulablePods' | 'DeadNodes';
  state: OperationState;
  filter?: AuditOperationFilter;
  skippedInstances?: SkippedInstance[];
  description?: string;
};

export type AuditRebalancingOperation = Omit<
  AuditOperation,
  'effect' | 'node'
> &
  (
    | {
        effect: OperationEffects.ADD;
        addAttempts: AuditNode[];
      }
    | {
        effect:
          | OperationEffects.DRAIN
          | OperationEffects.REMOVE
          | OperationEffects.PREPARE;
        node: AuditNode;
      }
  );

type AuditNodeConfiguration = {
  default: boolean;
  id: string;
  name: string;
  version: number;
};

type ClusterChange = {
  cluster: AuditClusterMinimal;
  addedNodes: AuditNode[];
  deletedNodes: AuditNode[];
  node: AuditNode;
};

type ScheduledRebalancingJobChange = {
  cluster: {
    id: string;
  };
  details: string;
  plan?: {
    id: string;
  };
  schedule: {
    id: string;
    name: string;
  };
};

export type ClusterHibernationEvent = {
  cluster: { id: string };
  clusterHibernationJob?: {
    id: string;
  };
  hibernationSchedule: { id: string } | null;
  type: ClusterHibernationJobAuditLogEventType;
};

export type AuditEvent<E, ET, L = AuditLogLabels | undefined> = {
  time: string;
  id: string;
  initiatedBy: InitiatedBy;
  event: E & { error?: AuditError; message?: string };
  eventType: ET;
  labels: L;
};

export type NodeAddedEvent = AuditEvent<
  { cluster: AuditExternalClusterMinimal; node: AuditNode },
  EventType.NODE_ADDED
>;

export type NodesUpdatedEvent = AuditEvent<
  ClusterChange,
  EventType.NODES_UPDATED
>;

export type NodeDeletedEvent = AuditEvent<
  { cluster: AuditExternalClusterMinimal; node: AuditNode },
  EventType.NODE_DELETED
>;

export type ClusterCreatedEvent = AuditEvent<
  ClusterChange,
  EventType.CLUSTER_CREATED
>;

export type ClusterConnectedEvent = AuditEvent<
  { cluster: AuditExternalClusterMinimal },
  EventType.CLUSTER_CONNECTED
>;

export type ClusterUpdatedEvent = AuditEvent<
  { cluster: AuditExternalClusterMinimal },
  EventType.CLUSTER_UPDATED
>;

export type ClusterPausedEvent = AuditEvent<
  {
    cluster: AuditClusterMinimal | AuditExternalClusterMinimal;
  },
  EventType.CLUSTER_PAUSED
>;

export type ClusterResumedEvent = AuditEvent<
  {
    cluster: AuditClusterMinimal | AuditExternalClusterMinimal;
  },
  EventType.CLUSTER_RESUMED
>;

export type ClusterDeletedEvent = AuditEvent<
  {
    cluster: AuditClusterMinimal;
  },
  EventType.CLUSTER_DELETED
>;

export type ClusterDisconnectedEvent = AuditEvent<
  { cluster: AuditExternalClusterMinimal },
  EventType.CLUSTER_DISCONNECTED
>;

export type ClusterReconcileTriggeredEvent = AuditEvent<
  { cluster: AuditExternalClusterMinimal },
  EventType.CLUSTER_RECONCILE_TRIGGERED
>;

export type ClusterHibernationJobFinishedEvent = AuditEvent<
  ClusterHibernationEvent,
  EventType.CLUSTER_HIBERNATION_JOB_FINISHED
>;

export type ClusterHibernationJobPendingEvent = AuditEvent<
  ClusterHibernationEvent,
  EventType.CLUSTER_HIBERNATION_JOB_PENDING
>;

export type ClusterHibernationJobFailedEvent = AuditEvent<
  ClusterHibernationEvent,
  EventType.CLUSTER_HIBERNATION_JOB_FAILED
>;

export type ClusterHibernationTriggeredEvent = AuditEvent<
  { cluster: AuditExternalClusterMinimal; nodes: AuditNode[] },
  EventType.CLUSTER_HIBERNATION_TRIGGERED
>;

export type ClusterHibernationScheduleFailedEvent = AuditEvent<
  ClusterHibernationEvent,
  EventType.CLUSTER_HIBERNATION_SCHEDULE_FAILED
>;

export type ClusterHibernatedEvent = AuditEvent<
  { cluster: AuditExternalClusterMinimal },
  EventType.CLUSTER_HIBERNATED
>;

export type PolicyAppliedEvent = AuditEvent<
  {
    policy: AuditPolicy;
    policyAction: string;
  } & ClusterChange,
  EventType.POLICY_APPLIED
>;

export type NodeInterruptedEvent = AuditEvent<
  Omit<ClusterChange, 'addedNodes'> | ExternalNodeInterrupt,
  EventType.NODE_INTERRUPTED
>;

export type PolicyUpdatedEvent = AuditEvent<
  {
    cluster: AuditCluster;
    changelog: ChangeLog[];
  },
  EventType.POLICY_UPDATED
>;

export type AutoscalerExecutedEvent = AuditEvent<
  {
    cluster: AuditCluster;
    ops: AuditOperation[];
    trigger?: Trigger;
  },
  EventType.AUTOSCALER_EXECUTED
>;

export type NodeDrainedEvent = AuditEvent<
  ClusterChange,
  EventType.NODE_DRAINED
>;

export type RebalancingPlanInitiatedEvent = AuditEvent<
  ClusterChange,
  EventType.REBALANCING_PLAN_INITIATED
>;

export type RebalancingPlanExecutedEvent = AuditEvent<
  ClusterChange,
  EventType.REBALANCING_PLAN_EXECUTED
>;

export type RebalancingNodeOperations = AuditEvent<
  {
    cluster: AuditCluster;
    ops: AuditRebalancingOperation[];
  },
  EventType.REBALANCING_NODE_OPERATIONS
>;

export type RebalancingNodeOperationFinished = AuditEvent<
  {
    cluster: AuditCluster;
    op: AuditRebalancingOperation;
  },
  EventType.REBALANCING_NODE_OPERATION_FINISHED
>;

export type RebalancingFailedEvent = AuditEvent<
  {
    cluster: { id: string };
    rebalancingPlanId: string;
    details: string;
    reason: string;
    status: string;
    errors: Array<{ message: string; reason: string }>;
  },
  EventType.REBALANCING_PLAN_FAILED
>;

export type ScheduledRebalancingSkippedEvent = AuditEvent<
  ScheduledRebalancingJobChange,
  EventType.SCHEDULED_REBALANCING_SKIPPED
>;

export type NodeConfigurationCreateEvent = AuditEvent<
  {
    cluster: AuditClusterMinimal;
    NodeConfiguration: AuditNodeConfiguration;
  },
  EventType.NODE_CONFIGURATION_CREATED
>;

export type NodeConfigurationUpdateEvent = AuditEvent<
  {
    cluster: AuditClusterMinimal;
    NodeConfiguration: AuditNodeConfiguration;
  },
  EventType.NODE_CONFIGURATION_UPDATED
>;

export type NodeConfigurationDeleteEvent = AuditEvent<
  {
    cluster: AuditClusterMinimal;
    NodeConfiguration: AuditNodeConfiguration;
  },
  EventType.NODE_CONFIGURATION_DELETED
>;

export type NodeConfigurationAsDefaultEvent = AuditEvent<
  {
    cluster: AuditClusterMinimal;
    NodeConfiguration: AuditNodeConfiguration;
  },
  EventType.NODE_CONFIGURATION_DEFAULT
>;

export type CloudInstanceCreatedEvent = AuditEvent<
  {
    cluster: AuditExternalClusterMinimal;
    node: AuditNode;
  },
  EventType.CLOUD_INSTANCE_CREATED
>;

export type AddBlackListExecutedEvent = AuditEvent<
  {
    enabledInstance: null | string;
    // support two different naming patterns
    disabledInstance: {
      Criteria?: {
        instanceFamily?: string;
        instanceType?: string;
        lifecycle: string;
      };
      criteria?: {
        instanceFamily?: string;
        lifecycle: string;
        instanceType?: string;
      };
      DisabledAt?: string;
      disabledAt?: string;
      ExpiresAt?: string;
      expiresAt?: string;
      Reason?: string;
      reason?: string;
    };
  },
  EventType.ADD_BLACKLIST_EXECUTED
>;

export type NodeTemplateCreatedEvent = AuditEvent<
  NoteTemplate,
  EventType.NODE_TEMPLATE_CREATED
>;

export type NodeTemplateUpdatedEvent = AuditEvent<
  {
    isDefault: boolean;
    name: string;
    changelog: NodeTemplateChangeLog[];
  },
  EventType.NODE_TEMPLATE_UPDATED
>;

export type NodeTemplateDeletedEvent = AuditEvent<
  NoteTemplate,
  EventType.NODE_TEMPLATE_DELETED
>;

type BaseCommitmentEvent = {
  assignment_info?: {
    cluster_info: {
      cloud: string;
      id: string;
      organization_id: string;
      region: string;
    };
    priority_rank: number;
  };
  commitment_id: string;
  commitment_name: string;
};

export type CommitmentAssignedEvent = AuditEvent<
  BaseCommitmentEvent,
  EventType.COMMITMENT_ASSIGNED
>;

export type CommitmentUnassignedEvent = AuditEvent<
  BaseCommitmentEvent,
  EventType.COMMITMENT_UNASSIGNED
>;

export type CommitmentAssignmentChangedEvent = AuditEvent<
  {
    commitment_id: string;
    commitment_name: string;
    new_assignment_order: {
      cluster_info: {
        cloud: string;
        id: string;
        organization_id: string;
        region: string;
      };
      priority_rank: number;
    };
    labels: {
      clusterId: string;
      traceId: string;
    };
  },
  EventType.COMMITMENT_ASSIGNMENT_CHANGED
>;

export type InterruptionPredictionNodesToRebalanceEvent = AuditEvent<
  {
    cluster: AuditCluster;
    ops?: AuditRebalancingOperation[];
  },
  EventType.INTERRUPTION_PREDICTION_NODES_TO_REBALANCE
>;

export type InterruptionPredictionReceivedEvent = AuditEvent<
  {
    cluster: AuditExternalClusterMinimal;
    node: AuditNode;
  },
  EventType.INTERRUPTION_PREDICTION_RECEIVED
>;

export type DboAuditLogEventType =
  | 'dboDirectModeEnabled'
  | 'dboDirectModeDisabled'
  | 'dboDatabaseAdded'
  | 'dboDatabaseRemoved'
  | 'dboDatabaseReconfigured'
  | 'dboTableRuleAdded'
  | 'dboTableRuleUpdated'
  | 'dboTableRuleRemoved'
  | 'dboQueryRuleAdded'
  | 'dboQueryRuleUpdated'
  | 'dboQueryRuleRemoved'
  | 'dboProxyDatabaseUpdated';

export type DboAuditLogEvent = AuditEvent<
  {
    databaseName?: string;
    directMode?: boolean;
    before?: Record<string, string | undefined>;
    after?: Record<string, string | undefined>;
  },
  DboAuditLogEventType,
  | {
      traceId: string;
      databaseId?: string;
      databaseInstanceId?: string;
      cacheGroupId: string;
      cacheConfigurationId?: string;
    }
  | {
      traceId: string;
      databaseInstanceId: string;
      proxyId: string;
      databaseId: string;
    }
  | undefined
>;

type UnknownEvent = AuditEvent<ClusterChange, string>;

export type AuditLogEntry =
  | UnknownEvent
  | ClusterConnectedEvent
  | ClusterCreatedEvent
  | ClusterUpdatedEvent
  | ClusterPausedEvent
  | ClusterResumedEvent
  | ClusterDeletedEvent
  | ClusterDisconnectedEvent
  | ClusterReconcileTriggeredEvent
  | ClusterHibernationJobFinishedEvent
  | ClusterHibernationJobPendingEvent
  | ClusterHibernationJobFailedEvent
  | ClusterHibernationTriggeredEvent
  | ClusterHibernationScheduleFailedEvent
  | ClusterHibernatedEvent
  | NodeAddedEvent
  | NodeInterruptedEvent
  | NodeDeletedEvent
  | NodesUpdatedEvent
  | PolicyAppliedEvent
  | PolicyUpdatedEvent
  | AutoscalerExecutedEvent
  | NodeDrainedEvent
  | RebalancingPlanInitiatedEvent
  | RebalancingPlanExecutedEvent
  | RebalancingNodeOperations
  | RebalancingNodeOperationFinished
  | RebalancingFailedEvent
  | ScheduledRebalancingSkippedEvent
  | NodeConfigurationCreateEvent
  | NodeConfigurationUpdateEvent
  | NodeConfigurationDeleteEvent
  | NodeConfigurationAsDefaultEvent
  | CloudInstanceCreatedEvent
  | AddBlackListExecutedEvent
  | NodeTemplateCreatedEvent
  | NodeTemplateUpdatedEvent
  | NodeTemplateDeletedEvent
  | CommitmentAssignedEvent
  | CommitmentUnassignedEvent
  | CommitmentAssignmentChangedEvent
  | InterruptionPredictionNodesToRebalanceEvent
  | InterruptionPredictionReceivedEvent
  | DboAuditLogEvent;

export type AuditLogListEntry = CastaiAuditV1beta1AuditListEntry;

export type AuditV2Event = CastaiAuditV2AuditEvent;

export type AuditV2ListParams = AuditV2APIApiAuditV2APIListAuditEventsRequest;

export type AuditV2HistogramParams =
  AuditV2APIApiAuditV2APIGetAuditHistogramRequest;

export type AuditV2StatsParams = AuditV2APIApiAuditV2APIGetAuditStatsRequest;

export type AuditV2EventCounter = CastaiAuditV2EventCounter;
